/**
 * Locale-aware formatting. Pure functions: the caller passes the active
 * language (`useTranslation().i18n.resolvedLanguage`), so a component
 * re-renders with the right format when the user switches language. Never
 * build an `Intl` formatter at module level: it would freeze the language
 * the module was loaded in.
 */

export function formatNumber(value, locale, options = {}) {
  return new Intl.NumberFormat(locale, options).format(Number(value));
}

export function formatDate(value, locale, options = { day: 'numeric', month: 'short', year: 'numeric' }) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(value));
}

export function formatDateTime(value, locale, options = { dateStyle: 'medium', timeStyle: 'short' }) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(value));
}

export function formatTime(value, locale, options = { hour: '2-digit', minute: '2-digit' }) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(value));
}

const RELATIVE_UNITS = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/** "3 days ago" / "il y a 3 jours" / "hace 3 días", relative to `now`. */
export function formatRelativeTime(value, locale, now = Date.now()) {
  const seconds = Math.round((new Date(value).getTime() - now) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size || unit === 'second') {
      return formatter.format(Math.round(seconds / size), unit);
    }
  }
  throw new Error('unreachable');
}
